/**
 * O modelo de um cliente da plataforma (uma clínica, um escritório, uma loja).
 * Tudo no sistema é escopado por tenant desde o primeiro dia.
 */

export type DiaSemana = "dom" | "seg" | "ter" | "qua" | "qui" | "sex" | "sab";

export const DIAS: DiaSemana[] = ["dom", "seg", "ter", "qua", "qui", "sex", "sab"];

export const NOME_DO_DIA: Record<DiaSemana, string> = {
  dom: "domingo",
  seg: "segunda-feira",
  ter: "terça-feira",
  qua: "quarta-feira",
  qui: "quinta-feira",
  sex: "sexta-feira",
  sab: "sábado",
};

export interface Profissional {
  id: string;
  nome: string;
  especialidade: string;
  /** Dias em que atende. É isto que impede o agente de oferecer um dia vazio. */
  dias: DiaSemana[];
  /** Unidades onde atende. Vazio = todas. */
  unidades: string[];
}

export interface Servico {
  id: string;
  nome: string;
  /** Uma linha, em português de gente. Vai para a constituição. */
  descricao: string;
  /**
   * Como o preço é dito ao cliente. Texto, porque negócio real tem
   * "a partir de", pacote e "sob avaliação" — e o agente precisa
   * repetir exatamente isto, sem arredondar nem inventar.
   */
  preco: string;
  duracaoMin: number;
  /** ids de Profissional. Vazio = qualquer um atende. */
  profissionais: string[];
  /** Unidades onde é oferecido. Vazio = todas. */
  unidades: string[];
}

export interface Expediente {
  dia: DiaSemana;
  /** "09:00" — null nos dois campos significa fechado. */
  abre: string | null;
  fecha: string | null;
}

/**
 * Uma unidade física da empresa.
 *
 * Existe porque endereço não é detalhe de cadastro: Maringá e Marialva ficam a
 * 25 km uma da outra, e paciente mandado para a cidade errada não volta. Cada
 * unidade tem o próprio expediente e a própria equipe, e o agente precisa saber
 * de qual está falando ANTES de oferecer horário.
 */
export interface Unidade {
  id: string;
  /** Como o cliente chama o lugar: "Maringá", "Centro", "Unidade 2". */
  nome: string;
  endereco?: string;
  telefone?: string;
  /** O expediente DESTA unidade. Uma pode abrir sábado e a outra não. */
  expediente: Expediente[];
  /** Agenda própria. Vazio = a agenda do tenant, com a unidade marcada no evento. */
  calendarId?: string;
}

export interface EtapaFunil {
  id: string;
  rotulo: string;
  /** O que esta etapa quer dizer, em uma frase. Aparece no painel. */
  definicao: string;
}

export interface Tenant {
  id: string;
  slug: string;
  nome: string;
  /** IANA, ex.: "America/Sao_Paulo". Toda data do agente passa por aqui. */
  timezone: string;

  /**
   * "arquivado" para o WhatsApp de responder e some do seletor do dono, mas o
   * cadastro inteiro continua no disco — é o passo reversível antes de
   * excluir de vez. Cadastro antigo não tem este campo; o store normaliza
   * para "ativo" na leitura.
   */
  status: "ativo" | "arquivado";

  agente: {
    /** Como o agente se apresenta. */
    nome: string;
    /** O bloco OBJETIVO do prompt. */
    objetivo: string;
    /** O bloco de tom de voz. */
    tom: string;
  };

  contato: {
    endereco?: string;
    telefone?: string;
    instagram?: string;
    email?: string;
  };

  /** Sempre com pelo menos uma. Quem lê recebe já normalizado pelo store. */
  unidades: Unidade[];

  /**
   * @deprecated Expediente do tempo em que a empresa era uma só. Continua sendo
   * lido para migrar cadastros antigos; o expediente de verdade é o da unidade.
   */
  expediente?: Expediente[];

  profissionais: Profissional[];
  servicos: Servico[];
  funil: EtapaFunil[];

  /**
   * De onde vêm agenda e ficha para ESTE tenant.
   *
   * "memoria" roda sem credencial nenhuma e não escreve em lugar nenhum —
   * é o modo do sandbox e do testador. "google" fala com a conta real da
   * empresa. O interruptor é por tenant porque um cliente pode estar em
   * produção enquanto outro ainda está sendo configurado.
   */
  integracoes: {
    agenda: "memoria" | "google";
    ficha: "memoria" | "google";
    /** Qual provedor de WhatsApp atende este cliente. */
    whatsapp: "sandbox" | "cloud" | "zapi" | "evolution" | "evolution-go";
    /** id do Google Agenda. Normalmente o e-mail da agenda da empresa. */
    calendarId?: string;
    /** id da planilha, o trecho da URL entre /d/ e /edit. */
    spreadsheetId?: string;
    /** Nome da aba dentro da planilha. */
    aba?: string;
  };

  /** O que o sistema manda sozinho. Cadastro antigo não tem; o store normaliza. */
  avisos?: Avisos;

  agenda: {
    /** Passo da grade de horários, em minutos. */
    passoMin: number;
    /** Antecedência mínima para agendar, em horas. */
    antecedenciaMinHoras: number;
    /** Até quantos dias à frente o agente pode oferecer. */
    janelaDias: number;
  };
}

/**
 * O que sai sem ninguém apertar botão — por isso tudo começa DESLIGADO: a
 * clínica decide ligar. "Não configurado significa não enviar" (README).
 */
export interface Avisos {
  /**
   * WhatsApp de quem recebe o aviso quando o agente passa uma conversa para a
   * equipe (só dígitos, com DDI). Vazio: o aviso fica só no painel. Mensagem
   * que CHEGA deste número não é de paciente — o agente não responde.
   */
  equipeWhatsApp: string;
  /** Uma mensagem ao paciente antes da consulta, pelo WhatsApp da clínica. */
  lembrete: { ativo: boolean; horasAntes: number };
}

export const AVISOS_PADRAO: Avisos = { equipeWhatsApp: "", lembrete: { ativo: false, horasAntes: 24 } };

/** Os avisos do tenant, sempre inteiros — mesmo em cadastro de antes de existirem. */
export function avisosDo(t: Pick<Tenant, "avisos">): Avisos {
  return {
    equipeWhatsApp: t.avisos?.equipeWhatsApp?.replace(/\D/g, "") ?? "",
    lembrete: { ...AVISOS_PADRAO.lembrete, ...t.avisos?.lembrete },
  };
}

/** O funil padrão — o tenant pode sobrescrever. */
export const FUNIL_PADRAO: EtapaFunil[] = [
  { id: "entrou", rotulo: "Entrou", definicao: "Mandou a primeira mensagem." },
  { id: "respondeu", rotulo: "Respondeu", definicao: "Voltou a escrever depois da primeira resposta do agente." },
  { id: "qualificado", rotulo: "Qualificado", definicao: "Disse qual serviço quer." },
  { id: "agendou", rotulo: "Agendou", definicao: "Tem horário marcado na agenda." },
  { id: "compareceu", rotulo: "Compareceu", definicao: "Alguém da equipe confirmou que a pessoa apareceu." },
];
